import * as React from 'react';

import { Spinner } from '@/components/ui/spinner/spinner';
import { cn } from '@/utils';

import { ChatInput } from './chat-input';
import { ChatMessageItem } from './chat-message-item';
import { BhanzuBuddyEmptyState as ExternalEmptyState } from './empty-state';
import { BhanzuBuddyHeader } from './header';

interface Message {
  id: string;
  type: 'user' | 'buddy';
  content: string;
}

interface OpenViewProps {
  chatState: 'opening' | 'open' | 'closing';
  handleToggleOpen: () => void;
  chatInterfaceRef: React.RefObject<HTMLDivElement>;
  className?: string;
  props: any;
  currentMode: 'explain' | 'guide';
  handleToggleMode: (mode: 'explain' | 'guide') => void;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  messages: Message[];
  isInitialLoading: boolean;
  isHistoryLoading?: boolean;
  onFeedback: (messageId: string, feedback: 'like' | 'dislike') => void;
  onGuideMe: (message: string) => void;
  isBraveBrowser: boolean;
  handleChatInputSend: (message: string) => void;
  isLoading: boolean;
  message: string;
  setMessage: (message: string) => void;
  ref: React.ForwardedRef<HTMLDivElement>;
}

export const OpenView = ({
  chatState,
  handleToggleOpen,
  chatInterfaceRef,
  className,
  props,
  currentMode,
  handleToggleMode,
  chatContainerRef,
  messages,
  isInitialLoading,
  isHistoryLoading = false,
  onFeedback,
  onGuideMe,
  isBraveBrowser,
  handleChatInputSend,
  isLoading,
  message,
  setMessage,
  ref,
}: OpenViewProps) => {
  const isOpen = chatState === 'open' || chatState === 'opening';

  const renderMessages = () => {
    if (isInitialLoading || isHistoryLoading) {
      return (
        <div
          className="h-full min-h-96 flex items-center justify-center"
          data-testid="bhanzu-buddy-initial-loader"
        >
          <Spinner size="lg" />
        </div>
      );
    }
    if (messages.length === 0) {
      return <ExternalEmptyState currentMode={currentMode} />;
    }
    return messages.map(msg => (
      <ChatMessageItem
        key={msg.id}
        message={msg}
        onFeedback={onFeedback}
        onGuideMe={onGuideMe}
        currentMode={currentMode}
      />
    ));
  };

  return (
    <>
      {!!isBraveBrowser && !isOpen && (
        <div className="fixed bottom-2 left-2 right-2 md:left-4 md:right-4 z-30 bg-amber-500/90 text-black rounded-lg px-3 py-2 text-xs md:text-sm pointer-events-none">
          <strong>Brave Browser:</strong> Voice input may not work due to
          privacy settings. Try Chrome or Firefox.
        </div>
      )}
      <button
        type="button"
        className={cn(
          'fixed inset-0 bg-black/50 z-(--z-overlay) transition-opacity duration-500 ease-out border-none p-0 cursor-default',
          chatState === 'opening'
            ? 'opacity-0 animate-in fade-in duration-500'
            : '',
          chatState === 'open' ? 'opacity-100' : '',
          chatState === 'closing'
            ? 'opacity-0 animate-out fade-out duration-400'
            : ''
        )}
        onClick={handleToggleOpen}
        aria-label="Close chat"
      />
      <div
        ref={node => {
          if (chatInterfaceRef) {
            (
              chatInterfaceRef as React.RefObject<HTMLDivElement | null>
            ).current = node;
          }
          if (typeof ref === 'function') {
            ref(node);
          }
        }}
        className={cn(
          'fixed z-(--z-modal) flex flex-col bg-magical-moonlight-d4 rounded-2xl overflow-hidden',
          'w-[98dvw] lg:w-full max-w-4xl h-[80vh] max-h-[600px] min-h-96',
          'lg:mx-4 left-1/2 bottom-4 -translate-x-1/2',
          chatState === 'opening'
            ? 'animate-in slide-in-from-bottom-4 zoom-in-95 duration-500 ease-out origin-bottom'
            : '',
          chatState === 'closing'
            ? 'animate-out slide-out-to-bottom-4 zoom-out-95 duration-400 ease-in origin-bottom'
            : '',
          className
        )}
        {...props}
      >
        <BhanzuBuddyHeader
          currentMode={currentMode}
          onToggleMode={handleToggleMode}
          onClose={handleToggleOpen}
        />
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6"
        >
          {renderMessages()}
        </div>
        {!!isBraveBrowser && (
          <div className="px-4 sm:px-6 py-2 bg-amber-500/10 border-l-4 border-amber-500">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-amber-500 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-amber-200">
                  <strong>Brave Browser Detected:</strong> Voice input may not
                  work due to privacy settings. Try Chrome or Firefox for voice
                  features.
                </p>
              </div>
            </div>
          </div>
        )}
        <ChatInput
          onSendMessage={handleChatInputSend}
          isLoading={isLoading}
          placeholder="Type here or talk..."
          initialMessage={message}
          onMessageChange={setMessage}
        />
      </div>
    </>
  );
};
